/**
 * "视图"菜单：缩放/参考线/对齐（DESIGN.md §6.5 补充，参照 Photoshop）。
 *
 * 标尺开关（`view.toggleRulers`）已经在 main.tsx 里注册，这里补的是
 * 缩放命令菜单入口、参考线增删/锁定、对齐（吸附）总开关。
 *
 * 缩放：Ctrl+0/1/+/- 已经在 `CanvasView.tsx` 自己的 keydown 监听器里
 * 直接处理（不经过这套 action registry）——这里注册的菜单项**不带
 * shortcut**，只是给"点菜单"这条路一个入口，避免同一次按键被两条路
 * 各处理一遍（重复缩放两档）。真正执行落在 `deps.requestViewCommand`，
 * 由装配层转给 CanvasView 的 `viewCommandRequest` prop。
 */
import type { ActionRegistry } from './registry';
import type { AppStore } from '../store';
import { clearGuides, setGuidesLocked } from '../guides';

/**
 * 缩放命令的种类，只取菜单需要的这四种（不含 pan/rotate/zoomBy）。
 * 不从 `interaction/viewport` import 真正的 `ViewCommand`——那是上层
 * （interaction）的类型，`app` 层不允许反向依赖它（CLAUDE.md 规则 4）。
 * 装配层（main.tsx，本身就在 interaction 之上）负责把这个种类映射成
 * 真正的 `ViewCommand` 再转给 `CanvasView`。
 */
export type ZoomCommandKind = 'zoomIn' | 'zoomOut' | 'fit' | 'actualPixels';

export interface ViewActionDeps {
  readonly store: AppStore;
  readonly requestViewCommand: (kind: ZoomCommandKind) => void;
  readonly onRequestNewGuide: () => void;
  readonly guidesLocked: () => boolean;
  readonly setGuidesLockedState: (locked: boolean) => void;
  readonly snapEnabled: () => boolean;
  readonly setSnapEnabled: (enabled: boolean) => void;
}

export function registerViewActions(registry: ActionRegistry, deps: ViewActionDeps): void {
  registry.register({
    id: 'view.zoomIn',
    title: '放大',
    group: '视图',
    section: 1,
    order: 0,
    enabled: (s) => s.hasDocument,
    run: () => deps.requestViewCommand('zoomIn'),
  });
  registry.register({
    id: 'view.zoomOut',
    title: '缩小',
    group: '视图',
    section: 1,
    order: 1,
    enabled: (s) => s.hasDocument,
    run: () => deps.requestViewCommand('zoomOut'),
  });
  registry.register({
    id: 'view.fitWindow',
    title: '按屏幕大小缩放',
    group: '视图',
    section: 1,
    order: 2,
    enabled: (s) => s.hasDocument,
    run: () => deps.requestViewCommand('fit'),
  });
  registry.register({
    id: 'view.actualPixels',
    title: '实际像素',
    group: '视图',
    section: 1,
    order: 3,
    enabled: (s) => s.hasDocument,
    run: () => deps.requestViewCommand('actualPixels'),
  });

  registry.register({
    id: 'view.newGuide',
    title: '新建参考线…',
    group: '视图',
    section: 2,
    order: 0,
    enabled: (s) => s.hasDocument,
    run: () => deps.onRequestNewGuide(),
  });
  registry.register({
    id: 'view.clearGuides',
    title: '清除参考线',
    group: '视图',
    section: 2,
    order: 1,
    enabled: (s) => s.hasDocument,
    run: (services) => {
      if (!clearGuides(deps.store)) services.hint('没有参考线');
    },
  });
  registry.register({
    id: 'view.toggleGuidesLocked',
    title: '锁定参考线',
    group: '视图',
    section: 2,
    order: 2,
    shortcut: { key: ';', mod: true, alt: true },
    enabled: (s) => s.hasDocument,
    run: (services) => {
      const next = !deps.guidesLocked();
      deps.setGuidesLockedState(next);
      setGuidesLocked(deps.store, next); // 已有的参考线跟着一起翻转，不是只影响以后新建的
      services.hint(next ? '参考线已锁定' : '参考线已解锁');
    },
  });

  registry.register({
    id: 'view.toggleSnap',
    title: '对齐',
    group: '视图',
    section: 3,
    order: 0,
    shortcut: { key: ';', mod: true, shift: true },
    run: (services) => {
      const next = !deps.snapEnabled();
      deps.setSnapEnabled(next);
      services.hint(next ? '已开启对齐' : '已关闭对齐');
    },
  });
}
